import { Button, Icon, Menu, SimpleDialog, Text } from "@ttc3k/trekker";
import {
  EnumOrganizationMetadataDisabledApps,
  useOrganizationNameByIdQuery,
} from "gql/generated.ts";
import {
  NavigationBlocker,
  SimpleErrorNotification,
  SimpleLoader,
} from "shared/components";
import {
  Calendar,
  KeyCommand,
  List,
  NavArrowDown,
  PathArrow,
  ProfileCircle,
  SelectWindow,
  Settings,
  ShopFourTiles,
  WarningTriangle,
} from "iconoir-react";
import { useCurrentAppRoute } from "shared/hooks";
import { useFeatureFlagContext } from "shared/contextProviders/featureFlags/useFeatureFlag";
import {
  ForwardRefExoticComponent,
  RefAttributes,
  SVGProps,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Divider, styled } from "styled-system/jsx";
import {
  getMultilingualString,
  getPreferredConnectedStorefrontId,
} from "shared/utils";
import { css } from "styled-system/css";
import { useAppContext } from "useAppContext";

type OrganizationMenuProps = {
  organizationId: string;
};

type MenuItem = {
  Icon: ForwardRefExoticComponent<
    Omit<SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
  >;
  key: string;
  path: string;
  iconClassName: string;
  disabled?: boolean;
  hidden?: boolean;
  featureFlagKey?: string;
};

const MENU_ITEMS: MenuItem[] = [
  {
    Icon: KeyCommand,
    key: "DIRECTORY",
    path: "directory",
    iconClassName: css({ color: "icon.accent.purple.mid!" }),
  },
  {
    Icon: ProfileCircle,
    key: "PEOPLE",
    path: "people",
    iconClassName: css({ color: "icon.success.mid!" }),
  },
  {
    Icon: List,
    key: "CUSTOM_LISTS",
    path: "custom-lists",
    iconClassName: css({ color: "icon.warning.mid!" }),
  },
  {
    Icon: Calendar,
    key: "EVENTS",
    path: "events",
    iconClassName: css({ color: "icon.accent.magenta.mid!" }),
  },
  {
    Icon: PathArrow,
    key: "TRIPPER",
    path: "tripper",
    iconClassName: css({ color: "icon.highlight.mid!" }),
    featureFlagKey: "tripper-enabled",
  },
  {
    Icon: SelectWindow,
    key: "WIDGETS",
    path: "widgets",
    iconClassName: css({ color: "icon.accent.blue.mid!" }),
  },
  {
    Icon: Settings,
    key: "ORGANIZATION_SETTINGS",
    path: "profile",
    iconClassName: css({ color: "icon.mid" }),
    hidden: true,
  },
];

export const OrganizationMenu = ({ organizationId }: OrganizationMenuProps) => {
  const { t } = useTranslation();
  const { data, error, loading } = useOrganizationNameByIdQuery({
    variables: { id: organizationId },
  });
  const appRoute = useCurrentAppRoute();
  const navigate = useNavigate();
  const {
    navigationBlockerDetails,
    organizations,
    setNavigationBlockerDetails,
  } = useAppContext();
  const organization = organizations.find(
    organization => organization._id === organizationId,
  );

  const { isEnabled } = useFeatureFlagContext();
  // The Events app is labelled "Events & Promotions" once Promotions is on for the org
  const appLabelKey = (key: string) =>
    key === "EVENTS" && isEnabled("promotions") ? "EVENTS_AND_PROMOTIONS" : key;
  const preferredStorefrontId =
    organization &&
    getPreferredConnectedStorefrontId(
      organization._id,
      organization.metadata?.connectedStorefront,
      organization.metadata?.connectedTestStorefront,
      organization.metadata?.connectedUsdStorefront,
      organization.metadata?.connectedUsdTestStorefront,
      organization.metadata?.storefrontMode,
    );

  const menuItems = useMemo(
    () => [
      ...MENU_ITEMS.filter(
        item =>
          !organization?.metadata?.disabledApps?.includes(
            item.key as EnumOrganizationMetadataDisabledApps,
          ) &&
          (!item.featureFlagKey || isEnabled(item.featureFlagKey)),
      ),
      ...(preferredStorefrontId
        ? [
            {
              Icon: ShopFourTiles,
              key: "STOREFRONT",
              path: `storefront/${preferredStorefrontId}`,
              iconClassName: css({ color: "icon.warning.mid!" }),
            },
          ]
        : []),
    ],
    [organization?.metadata?.disabledApps, isEnabled, preferredStorefrontId],
  );

  const [selectedItem, setSelectedItem] = useState(menuItems[0]);
  const [pendingRoute, setPendingRoute] = useState<string>();
  const [isBlockerOpen, setIsBlockerOpen] = useState(false);

  const handleAppSwitcherClick = (item: { value: string }) => {
    setIsMenuOpen(false);
    const nextItem =
      menuItems.find(menuItem => menuItem.path === item.value) ?? menuItems[0];
    const route = item.value
      ? `${appRoute.root}/${item.value}`
      : `${appRoute.root}`;

    if (navigationBlockerDetails.isNavigationBlocked) {
      setPendingRoute(route);
      setIsBlockerOpen(true);
      return;
    }

    setSelectedItem(nextItem);
    navigate(route);
  };

  const handleStayHereClick = () => {
    setIsBlockerOpen(false);
    setPendingRoute(undefined);
    setNavigationBlockerDetails(prev => ({
      ...prev,
      isNavigationBlockerDismissed: true,
    }));
  };

  const handleContinueClick = () => {
    if (!pendingRoute) return;

    setIsBlockerOpen(false);
    setNavigationBlockerDetails({
      isNavigationBlocked: false,
      isNavigationBlockerDismissed: true,
    });
    navigate(pendingRoute);
    setPendingRoute(undefined);
  };

  useEffect(() => {
    setSelectedItem(
      menuItems.find(
        menuItem =>
          menuItem.path === appRoute.appPath ||
          menuItem.path.startsWith(appRoute.appPath + "/"),
      ) ?? menuItems[0],
    );
  }, [appRoute, menuItems]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  if (loading) return <SimpleLoader />;

  if (error) return <SimpleErrorNotification />;

  if (!data || !data.organizationById) {
    return null;
  }

  return (
    <>
      <Divider
        orientation={"vertical"}
        thickness={"2px"}
        height={"16px"}
        color={"border.light"}
      />
      <NavigationBlocker>
        <ReactRouterBaseLink
          to={`${appRoute.root}/home`}
          cursor={"pointer"}
          display="inline-block" // This is so the focus ring works correctly with the <p> tag inside the link
          borderRadius={"25"}
          _focusVisible={{
            outlineColor: "border.light",
            outlineOffset: "4px",
            outlineWidth: "1px",
            outlineStyle: "solid",
          }}
        >
          <Text
            visual={"smallMedium"}
            color={"text.dark"}
            className={css({ textAlign: "right" })}
          >
            {getMultilingualString(data.organizationById.name)}
          </Text>
        </ReactRouterBaseLink>
      </NavigationBlocker>
      {appRoute.appPath !== "home" && (
        <>
          <Divider
            orientation={"vertical"}
            thickness={"2px"}
            height={"16px"}
            color={"border.light"}
          />
          <Menu.Root
            open={isMenuOpen}
            onPointerDownOutside={() => setIsMenuOpen(false)}
            trigger={
              <Button
                visual="unstyled"
                color={"text.mid"}
                display={"flex!"}
                flexDir={"row"}
                alignItems={"center"}
                gap={75}
                borderRadius={"25"}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                <Text visual={"smallMedium"} flexShrink={0}>
                  {t(`core:${appLabelKey(selectedItem.key)}`)}
                </Text>
                <Icon Element={NavArrowDown} color={"icon.light"} />
              </Button>
            }
            positioning={{
              placement: "bottom",
              offset: {
                crossAxis: -75,
              },
            }}
            onSelect={(item: { value: string }) => handleAppSwitcherClick(item)}
          >
            {menuItems.map(
              item =>
                !item.hidden && (
                  <Menu.Item
                    value={item.path}
                    disabled={item.disabled}
                    justifyContent={"flex-start"}
                    gap={"200"}
                    key={item.key}
                  >
                    <Icon Element={item.Icon} className={item.iconClassName} />
                    <Text visual={"smallRegular"}>
                      {t(`core:${appLabelKey(item.key)}`)}
                    </Text>
                  </Menu.Item>
                ),
            )}
          </Menu.Root>
        </>
      )}
      <SimpleDialog
        open={isBlockerOpen}
        Icon={WarningTriangle}
        title={t("core:UNSAVED_CHANGES.TITLE")}
        message={t("core:UNSAVED_CHANGES.DESCRIPTION")}
        cancelButtonCopy={t("core:UNSAVED_CHANGES.STAY")}
        onCancelClick={handleStayHereClick}
        confirmButtonCopy={t("core:UNSAVED_CHANGES.CONTINUE")}
        confirmButtonVisual="destructive"
        onConfirmClick={handleContinueClick}
        size="md"
      />
    </>
  );
};

const ReactRouterBaseLink = styled(Link);
